package one.devsbox.www.ui.campaigns.detail

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import compose.assets.Svg
import compose.assets.svg.DeleteOutline
import compose.assets.svg.EditOutline
import compose.assets.svg.PauseCircle
import compose.assets.svg.PlayCircle
import kotlinx.coroutines.launch
import one.devsbox.shared.ads.CampaignState
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.io.Repository
import one.devsbox.www.ui.widgets.ScreenContentAction
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.www_portal.generated.resources.*
import tk.mallumo.compose.navigation.navTo_CampaignEUI
import tk.mallumo.compose.navigation.navTo_DeviceDUI
import tk.mallumo.compose.navigation.navTo_LocationDUI
import tk.mallumo.compose.navigation.navTo_ResourceUI
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState

@VM
class CampaignDVM : NavigationViewModel() {
    private lateinit var ctx: CampaignDCtx

    var contentActions by mutableStateOf<List<ScreenContentAction>>(emptyList())
        private set

    data class Data(
        val info: SharedIO.CampaignInfo
    )

    var state by mutableStateOf<DataState<Data>>(DataState.Loading())
        private set

    fun setup(ctx: CampaignDCtx) {
        this.ctx = ctx
        loadCampaignInfo()

    }

    private fun loadCampaignInfo() {
        state = DataState.Loading(state.entry)
        scope.launch {
            runCatching {
                Repository.ads.campaign.info(ctx.args.id)
            }.onSuccess {
                state = DataState.Result(Data(it))
            }.onFailure {
                state = DataState.Error(it.message ?: "Unknown error")
            }
            setupActions(state)
        }
    }

    interface Action {
        fun reload()
        fun pause()
        fun resume()
        fun edit()
        fun delete()
        fun back()

        fun itemClick(media: SharedIO.MediaCore)
        fun itemClick(location: SharedIO.CampaignInfo.Location)
        fun itemClick(device: SharedIO.CampaignInfo.Device)
    }

    val action = object : Action {
        override fun reload() {
            loadCampaignInfo()
        }

        override fun edit() {
            ctx.nav.parentNavigation?.navTo_CampaignEUI(ctx.args)
        }

        override fun pause() {
            changeState(CampaignState.PAUSED)
        }

        override fun resume() {
            changeState(CampaignState.ACTIVE)
        }

        override fun delete() {
            state = DataState.Loading(state.entry)
            scope.launch {
                runCatching {
                    Repository.ads.campaign.delete(ctx.args.id)
                }.onSuccess {
                    ctx.nav.up()
                }.onFailure {
                    state = DataState.Error(it.message ?: "Failed to delete campaign")
                }
            }
        }

        override fun back() {
            ctx.nav.up()
        }

        override fun itemClick(media: SharedIO.MediaCore) {
            ctx.nav.navTo_ResourceUI(ArgsId(media.id))
        }

        override fun itemClick(location: SharedIO.CampaignInfo.Location) {
            ctx.nav.navTo_LocationDUI(ArgsId(location.id))
        }

        override fun itemClick(device: SharedIO.CampaignInfo.Device) {
            ctx.nav.navTo_DeviceDUI(ArgsId(device.id))
        }
    }

    private fun changeState(newState: CampaignState) {
        state.entry?.info ?: return

        state = DataState.Loading(state.entry)
        scope.launch {
            runCatching {
                Repository.ads.campaign.change(ctx.args.id, newState)
            }.onSuccess {
                state = DataState.Result(Data(it))
            }.onFailure {
                state = DataState.Error(it.message ?: "Failed to update campaign state")
            }
            setupActions(state)
        }
    }

    private fun setupActions(result: DataState<Data>) {
        contentActions = result.entry?.info?.run {
            buildList {
                if (ctx.me.permission.campaign.write) {
                    if (CampaignState.ACTIVE match state) {
                        add(
                            ScreenContentAction(
                                text = Res.string.ui_campaigns_detail_btn_pause,
                                color = ctx.colorPrimaryContainer,
                                icon = Svg.PauseCircle
                            ) {
                                ctx.action.pause()
                            })
                    }
                    if (CampaignState.PAUSED match state) {
                        add(
                            ScreenContentAction(
                                text = Res.string.ui_campaigns_detail_btn_resume,
                                color = ctx.colorPrimaryContainer,
                                icon = Svg.PlayCircle
                            ) {
                                ctx.action.resume()
                            })
                    }
                    add(
                        ScreenContentAction(
                            text = Res.string.ui_campaigns_detail_btn_edit,
                            color = ctx.colorSecondaryContainer,
                            icon = Svg.EditOutline
                        ) {
                            ctx.action.edit()
                        })
                }
                if (ctx.me.permission.campaign.delete && !ctx.me.info.adminMode) {
                    add(
                        ScreenContentAction(
                            text = Res.string.ui_campaigns_detail_btn_delete,
                            color = ctx.colorErrorContainer,
                            icon = Svg.DeleteOutline
                        ) {
                            ctx.action.delete()
                        })
                }
            }
        } ?: emptyList()
    }

    override fun onRelease() {}
}

